import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthDeleteRequest, AuthGetRequest, AuthPostRequest } from '../Backend/APIMiddleware';

export const PAGE_SIZE = 10;

export async function queryDocumentHistory(docType, docId, pageSize, lastItem) {
    try {
        const queryParams = new URLSearchParams({ pageSize: String(pageSize) });
        if (lastItem) queryParams.append('lastItem', lastItem);

        const response = await AuthPostRequest(
            `/v1/document-history/${docType}/${docId}?${queryParams.toString()}`,
            {}
        );
        return response?.data || [];
    } catch (error) {
        console.error('Error fetching paged history:', error);
        throw error;
    }
}

export async function getSignedImageUrl(key, type, transactionId) {
    if (!key) return null;
    try {
        const response = await AuthGetRequest(
            `/v1/uploads/line-item-upload?key=${encodeURIComponent(key)}&ticketType=${type}&transactionId=${transactionId}`
        );
        return response?.data?.viewUrl || null;
    } catch (error) {
        console.error('Error fetching signed image URL:', error);
        return null;
    }
}

export function useTransactionDetails() {
    const { type, transactionId } = useParams();
    const navigate = useNavigate();

    const [customer, setCustomer] = useState(null);
    const [transaction, setTransaction] = useState(null);
    const [lineItems, setLineItems] = useState([]);
    const [payments, setPayments] = useState([]);
    const [layawayOption, setLayawayOption] = useState(false);

    const [isPageLoading, setIsPageLoading] = useState(true);
    const [pageError, setPageError] = useState(null);

    // Per-section loading flags (populated after transaction is known)
    const [isCustomerLoading, setIsCustomerLoading]     = useState(false);
    const [isLineItemsLoading, setIsLineItemsLoading]   = useState(false);
    const [isPaymentsLoading, setIsPaymentsLoading]     = useState(false);

    // ── History ──────────────────────────────────────────────────────────────
    const [ticketHistory, setTicketHistory] = useState([]);
    const [lastHistoryItem, setLastHistoryItem] = useState(null);
    const [hasMoreHistory, setHasMoreHistory] = useState(true);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const [isLoadingMoreHistory, setIsLoadingMoreHistory] = useState(false);
    const [historyError, setHistoryError] = useState(null);

    // ── Fetchers ─────────────────────────────────────────────────────────────
    const fetchTransactionDetails = useCallback(async () => {
        const response = await AuthGetRequest(`/v1/transactions/${type}/${transactionId}`);
        return response?.data || null;
    }, [type, transactionId]);

    const fetchCustomerDetails = useCallback(async (customerId) => {
        if (!customerId) return null;
        try {
            const response = await AuthGetRequest(`/v1/accounts?customerID=${customerId}`);
            return response?.data || null;
        } catch (error) {
            console.error('Error fetching customer details:', error);
            return null;
        }
    }, []);

    const fetchLineItems = useCallback(async () => {
        try {
            const response = await AuthGetRequest(`/v1/transactions/${type}/${transactionId}/line-items`);
            return response?.data || [];
        } catch (error) {
            console.error('Error fetching line items:', error);
            return [];
        }
    }, [type, transactionId]);

    const fetchPayments = useCallback(async () => {
        try {
            const response = await AuthGetRequest(`/v1/transactions/${type}/${transactionId}/payments`);
            return response?.data || [];
        } catch (error) {
            console.error('Error fetching payments:', error);
            return [];
        }
    }, [type, transactionId]);

    // ── Refresh helpers ───────────────────────────────────────────────────────
    const refreshTransactionAndLineItems = useCallback(async () => {
        const [transactionData, lineItemsData] = await Promise.all([
            fetchTransactionDetails(),
            fetchLineItems(),
        ]);
        setTransaction(transactionData);
        setLineItems(lineItemsData);
    }, [fetchTransactionDetails, fetchLineItems]);

    /**
     * Polls the backend for the latest payments + transaction state.
     * Used by CollectPaymentModal to detect when a Stripe QR payment lands.
     * Returns the updated payments array.
     */
    const pollForPayment = useCallback(async () => {
        const [transactionData, paymentsData] = await Promise.all([
            fetchTransactionDetails(),
            fetchPayments(),
        ]);
        setTransaction(transactionData);
        setPayments(paymentsData);
        return paymentsData;
    }, [fetchTransactionDetails, fetchPayments]);

    // ── Initial page load ─────────────────────────────────────────────────────
    useEffect(() => {
        let isMounted = true;

        const loadPage = async () => {
            // Reset everything
            setIsPageLoading(true);
            setPageError(null);
            setCustomer(null);
            setTransaction(null);
            setLineItems([]);
            setPayments([]);
            setTicketHistory([]);
            setLastHistoryItem(null);
            setHasMoreHistory(true);

            // ── Step 1: load the transaction — this gates the whole page ──────
            let transactionData;
            try {
                transactionData = await fetchTransactionDetails();
            } catch (error) {
                console.error('Error fetching transaction:', error);
                if (isMounted) { setPageError(error); setIsPageLoading(false); }
                return;
            }
            if (!isMounted) return;

            setTransaction(transactionData);
            setLayawayOption(Boolean(transactionData?.layawayContractKey));
            setIsPageLoading(false); // show the page immediately

            // ── Step 2: load customer, line items, payments in parallel ────────
            // Each resolves independently so the UI updates as each arrives.
            if (isMounted) {
                setIsCustomerLoading(true);
                setIsLineItemsLoading(true);
                setIsPaymentsLoading(true);
            }

            fetchCustomerDetails(transactionData?.customerId)
                .then(data  => { if (isMounted) setCustomer(data); })
                .catch(() => {})
                .finally(() => { if (isMounted) setIsCustomerLoading(false); });

            fetchLineItems()
                .then(data  => { if (isMounted) setLineItems(data || []); })
                .catch(() => {})
                .finally(() => { if (isMounted) setIsLineItemsLoading(false); });

            fetchPayments()
                .then(data  => { if (isMounted) setPayments(data || []); })
                .catch(() => {})
                .finally(() => { if (isMounted) setIsPaymentsLoading(false); });
        };

        loadPage();
        return () => { isMounted = false; };
    }, [fetchTransactionDetails, fetchCustomerDetails, fetchLineItems, fetchPayments]);

    // ── History ───────────────────────────────────────────────────────────────
    const fetchInitialTicketHistory = useCallback(async () => {
        try {
            setIsHistoryLoading(true);
            setHistoryError(null);
            const data = await queryDocumentHistory(type, transactionId, PAGE_SIZE, null);
            setTicketHistory(data);
            setLastHistoryItem(data.length > 0 ? data[data.length - 1].id : null);
            setHasMoreHistory(data.length === PAGE_SIZE);
        } catch (error) {
            console.error('Error fetching ticket history:', error);
            setTicketHistory([]);
            setLastHistoryItem(null);
            setHasMoreHistory(false);
            setHistoryError(error);
        } finally {
            setIsHistoryLoading(false);
        }
    }, [type, transactionId]);

    const fetchMoreTicketHistory = async () => {
        if (!lastHistoryItem || isLoadingMoreHistory) return;
        try {
            setIsLoadingMoreHistory(true);
            setHistoryError(null);
            const data = await queryDocumentHistory(type, transactionId, PAGE_SIZE, lastHistoryItem);
            setTicketHistory((prev) => [...prev, ...(data || [])]);
            setLastHistoryItem(data?.length > 0 ? data[data.length - 1].id : lastHistoryItem);
            setHasMoreHistory((data || []).length === PAGE_SIZE);
        } catch (error) {
            console.error('Error fetching more ticket history:', error);
            setHasMoreHistory(false);
            setHistoryError(error);
        } finally {
            setIsLoadingMoreHistory(false);
        }
    };

    // ── Line item mutations ───────────────────────────────────────────────────
    const saveLineItem = async (editingLineItem) => {
        const payload = {
            ...editingLineItem,
            cost: toNumberOrDefault(editingLineItem.cost, 0),
            discountAmount: toNumberOrDefault(editingLineItem.discountAmount, 0),
            weighIn: toNumberOrNull(editingLineItem.weighIn),
            weighOut: toNumberOrNull(editingLineItem.weighOut),
            quantity: toNumberOrDefault(editingLineItem.quantity, 1),
        };
        await AuthPostRequest(
            `/v1/transactions/${type}/${transactionId}/line-items/${editingLineItem.id}`,
            payload
        );
        await refreshTransactionAndLineItems();
    };

    const deleteLineItem = async (lineItemId) => {
        await AuthDeleteRequest(
            `/v1/transactions/${type}/${transactionId}/line-items/${lineItemId}`,
            {}
        );
        await refreshTransactionAndLineItems();
    };

    const createLineItem = async (newLineItem) => {
        const payload = {
            ...newLineItem,
            quantity: toNumberOrDefault(newLineItem.quantity, 1),
            cost: toNumberOrDefault(newLineItem.cost, 0),
            discountAmount: toNumberOrDefault(newLineItem.discountAmount, 0),
            weighIn: toNumberOrNull(newLineItem.weighIn),
            weighOut: toNumberOrNull(newLineItem.weighOut),
        };
        await AuthPostRequest(`/v1/transactions/${type}/${transactionId}/line-items`, payload);
        await refreshTransactionAndLineItems();
    };

    const uploadLineItemImage = async (file) => {
        const initResponse = await AuthPostRequest('/v1/uploads/line-item-upload', {
            fileName: file.name,
            contentType: file.type,
            ticketType: type,
            transactionId,
        });
        const { uploadUrl, key, imageUrl } = initResponse.data;
        await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': file.type },
            body: file,
        });
        return { key, imageUrl };
    };

    const deleteUploadedImage = async (key) => {
        await AuthDeleteRequest(
            `/v1/uploads/line-item-upload?key=${encodeURIComponent(key)}`,
            {}
        );
    };

    const updateStatus = async (newStatus) => {
        await AuthPostRequest(
            `/v1/transactions/${type}/${transactionId}/status`,
            { status: newStatus }
        );
        const transactionData = await fetchTransactionDetails();
        setTransaction(transactionData);
    };

    const recordPayment = async (payload) => {
        await AuthPostRequest(
            `/v1/transactions/${type}/${transactionId}/payments`,
            payload
        );
        const [transactionData, paymentsData] = await Promise.all([
            fetchTransactionDetails(),
            fetchPayments(),
        ]);
        setTransaction(transactionData);
        setPayments(paymentsData);
    };

    const updateTransactionDetails = async (payload) => {
        await AuthPostRequest(
            `/v1/transactions/${type}/${transactionId}`,
            payload
        );
        const transactionData = await fetchTransactionDetails();
        setTransaction(transactionData);
    };

    /**
     * Re-fetches the transaction from the backend and updates local state.
     * Used after uploading a receipt so the new receiptKey is reflected in the UI.
     */
    const refreshTransaction = useCallback(async () => {
        const data = await fetchTransactionDetails();
        if (data) setTransaction(data);
        return data;
    }, [fetchTransactionDetails]);

    const voidTransaction = async () => {
        await AuthPostRequest(
            `/v1/transactions/${type}/${transactionId}/void`,
            {}
        );
        const transactionData = await fetchTransactionDetails();
        setTransaction(transactionData);
    };

    /**
     * Generates (or regenerates) a layaway contract for this sale.
     * Sends the wizard payload to the backend, which creates the PDF,
     * stores it in S3, updates DynamoDB, and SMSes the customer.
     * Returns { contractKey, contractUrl }.
     */
    const generateLayawayContract = async (payload) => {
        const response = await AuthPostRequest(
            `/v1/transactions/${type}/${transactionId}/layaway-contract`,
            payload
        );
        const data = response?.data; // { contractKey, layawayContractKey, contractUrl, stripeInvoiceUrl }

        // Immediately patch the transaction state so the UI reflects the
        // new contract key without waiting on (or depending on) the re-fetch.
        const contractKeyFromResponse = data?.layawayContractKey || data?.contractKey;
        if (contractKeyFromResponse) {
            setTransaction((prev) => ({
                ...prev,
                layawayContractKey:         contractKeyFromResponse,
                layawayAllowOnlinePayments: data?.allowOnlinePayments ?? prev.layawayAllowOnlinePayments,
            }));
        }

        // Re-fetch the full updated record — but preserve the contract key from the
        // POST response in case DynamoDB read-after-write isn't immediate.
        try {
            const transactionData = await fetchTransactionDetails();
            if (transactionData) {
                setTransaction((prev) => ({
                    ...transactionData,
                    // Prefer the response data over a potentially stale re-fetch
                    layawayContractKey: transactionData.layawayContractKey || prev.layawayContractKey,
                }));
            }
        } catch (e) {
            console.warn('Could not re-fetch transaction after layaway generation:', e);
        }

        return data;
    };

    /**
     * Uploads a receipt PDF Blob to S3.
     * Flow:
     *   1. POST /receipt → backend stores receiptKey in DynamoDB, returns presigned PUT URL
     *   2. PUT blob directly to S3 using that URL
     * Returns { uploadUrl, key }.
     */
    const uploadReceipt = useCallback(async (blob) => {
        // Step 1 — get presigned URL + store key in DynamoDB
        const initResponse = await AuthPostRequest(
            `/v1/transactions/${type}/${transactionId}/receipt`,
            {}
        );
        const { uploadUrl, key } = initResponse.data;

        // Step 2 — upload the PDF blob directly to S3
        const putResponse = await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/pdf' },
            body: blob,
        });
        if (!putResponse.ok) {
            throw new Error(`S3 upload failed: ${putResponse.status} ${putResponse.statusText}`);
        }

        return { uploadUrl, key };
    }, [type, transactionId]);

    /**
     * Fetches a 24-hour pre-signed download URL for the stored receipt PDF.
     * Returns the URL string, or throws on error.
     */
    const getReceiptDownloadUrl = useCallback(async () => {
        const response = await AuthGetRequest(
            `/v1/transactions/${type}/${transactionId}/receipt`
        );
        return response?.data?.url;
    }, [type, transactionId]);

    /**
     * Sends a "ready for pickup" SMS to the customer for this repair.
     * Only valid when status is "Ready for Pickup".
     * Returns { smsSent: boolean, reason: string }, or throws on error.
     */
    const notifyCustomer = useCallback(async () => {
        const response = await AuthPostRequest(
            `/v1/transactions/${type}/${transactionId}/notify`,
            {}
        );
        return response?.data;
    }, [type, transactionId]);

    /**
     * Creates a Stripe Checkout Session for an in-store payment (employee use).
     * Works for both sales and repairs.
     * passCcFees — whether to add the flat 3% Stripe processing fee.
     * Returns the Stripe Checkout URL, or throws on error.
     */
    const createInStoreCheckout = async (amount, passCcFees = false) => {
        const response = await AuthPostRequest(
            `/v1/transactions/${type}/${transactionId}/instore-checkout`,
            { amount, passCcFees }
        );
        return response?.data?.checkoutUrl;
    };

    /**
     * Creates a pending PayPal POS payment record so the ZettleWebhookHandler
     * can correlate the incoming PurchaseCreated webhook back to this transaction.
     * The employee then charges the customer on the physical PayPal POS terminal.
     * Returns { pendingId }, or throws on error.
     */
    const createZettlePayment = async (amount) => {
        const response = await AuthPostRequest(
            `/v1/transactions/${type}/${transactionId}/zettle-checkout`,
            { amount }
        );
        return response?.data;
    };

    /**
     * Fetches a 24-hour pre-signed URL for the existing layaway contract.
     * Returns the URL string, or throws on error.
     */
    const getLayawayContractUrl = async () => {
        const response = await AuthGetRequest(
            `/v1/transactions/${type}/${transactionId}/layaway-contract`
        );
        return response?.data?.url;
    };

    /**
     * Creates a Stripe Checkout Session for a layaway payment.
     * Pass optional `amount` (USD float) for a specific installment; omit to
     * default to the full remaining balance.
     * Returns the Stripe Checkout URL string, or throws on error.
     */
    const getLayawayCheckoutUrl = async (amount = null) => {
        const payload = amount ? { amount } : {};
        const response = await AuthPostRequest(
            `/v1/transactions/${type}/${transactionId}/layaway-checkout`,
            payload
        );
        return response?.data?.checkoutUrl;
    };

    return {
        // identifiers
        type,
        transactionId,
        navigate,
        // data
        customer,
        transaction,
        lineItems,
        payments,
        layawayOption,
        setLayawayOption,
        // page state
        isPageLoading,
        pageError,
        isCustomerLoading,
        isLineItemsLoading,
        isPaymentsLoading,
        // history
        ticketHistory,
        hasMoreHistory,
        isHistoryLoading,
        isLoadingMoreHistory,
        historyError,
        fetchInitialTicketHistory,
        fetchMoreTicketHistory,
        // mutations
        saveLineItem,
        deleteLineItem,
        createLineItem,
        uploadLineItemImage,
        deleteUploadedImage,
        updateStatus,
        recordPayment,
        updateTransactionDetails,
        voidTransaction,
        generateLayawayContract,
        getLayawayContractUrl,
        getLayawayCheckoutUrl,
        notifyCustomer,
        createInStoreCheckout,
        createZettlePayment,
        pollForPayment,
        uploadReceipt,
        getReceiptDownloadUrl,
        refreshTransaction,
    };
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function toNumberOrDefault(value, defaultVal) {
    return value === '' || value === null || value === undefined ? defaultVal : Number(value);
}
function toNumberOrNull(value) {
    return value === '' || value === null || value === undefined ? null : Number(value);
}
